import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, DatePicker, Dropdown, Input } from 'antd';
import {
    DownOutlined,
    SearchOutlined,
    StarFilled,
    SwapOutlined,
    UpOutlined,
} from '@ant-design/icons';

const { RangePicker } = DatePicker;

const BRANDS = [{ id: 'gobananas', label: 'GoBananas' }];

export const EMPTY_REVIEW_FILTERS = {
    brands: [],
    ratings: [],
    onlyWithRating: false,
    withText: false,
    withMedia: false,
    orderStatuses: [],
    publicationStatuses: [],
    sort_dir: 'DESC',
    published_range: null,
};

const CATEGORIES = [
    { key: 'brands', label: 'Ваши кабинеты брендов' },
    { key: 'rating', label: 'Оценка' },
    { key: 'content', label: 'Контент' },
    { key: 'order', label: 'Статус получения' },
    { key: 'publication', label: 'Статус публикации' },
];

export const countSelectedFilters = (draft = EMPTY_REVIEW_FILTERS) => {
    let count = 0;
    count += (draft.brands || []).length;
    count += (draft.ratings || []).length;
    if (draft.onlyWithRating) count += 1;
    if (draft.withText) count += 1;
    if (draft.withMedia) count += 1;
    count += (draft.orderStatuses || []).length;
    count += (draft.publicationStatuses || []).length;
    return count;
};

const toggleInList = (list, value) => {
    const current = list || [];
    return current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];
};

const StarRow = ({ value }) => (
    <span className="inline-flex items-center gap-[2px]">
        {[1, 2, 3, 4, 5].map((star) => (
            <StarFilled
                key={star}
                style={{
                    fontSize: 15,
                    color: star <= value ? '#f5a623' : '#e6e8eb',
                }}
            />
        ))}
    </span>
);

const OptionRow = ({ checked, onChange, children }) => (
    <label className="flex cursor-pointer items-center gap-3 rounded-lg py-1.5 pr-2 hover:bg-[#f7f8fa]">
        <Checkbox checked={checked} onChange={onChange} />
        {children}
    </label>
);

const ReviewsFilterBar = ({ value, onApply }) => {
    const [open, setOpen] = useState(false);
    const [periodOpen, setPeriodOpen] = useState(false);
    const [category, setCategory] = useState('brands');
    const [brandQuery, setBrandQuery] = useState('');
    const [draft, setDraft] = useState(value || EMPTY_REVIEW_FILTERS);
    const rootRef = useRef(null);

    useEffect(() => {
        if (open) {
            setDraft(value || EMPTY_REVIEW_FILTERS);
            setBrandQuery('');
        }
    }, [open, value]);

    useEffect(() => {
        if (!open) return undefined;
        const onDocMouseDown = (event) => {
            if (!rootRef.current?.contains(event.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDocMouseDown);
        return () => document.removeEventListener('mousedown', onDocMouseDown);
    }, [open]);

    const selectedCount = useMemo(
        () => countSelectedFilters(value || EMPTY_REVIEW_FILTERS),
        [value]
    );
    const draftCount = useMemo(() => countSelectedFilters(draft), [draft]);

    const periodLabel = (() => {
        const range = value?.published_range;
        if (!range?.length || !range[0] || !range[1]) return 'Выбрать период';
        return `${range[0].format('DD.MM.YY')} — ${range[1].format('DD.MM.YY')}`;
    })();

    const pillClass = (active) =>
        `reviews-filter-pill ${active ? 'reviews-filter-pill--active' : ''}`;

    const applyDraft = () => {
        onApply?.({ ...draft });
        setOpen(false);
    };

    const resetDraft = () => {
        setDraft({
            ...EMPTY_REVIEW_FILTERS,
            sort_dir: draft.sort_dir || 'DESC',
            published_range: draft.published_range,
        });
    };

    const filteredBrands = BRANDS.filter((brand) =>
        brand.label.toLowerCase().includes(brandQuery.trim().toLowerCase())
    );

    const renderBody = () => {
        if (category === 'brands') {
            return (
                <>
                    <Input
                        allowClear
                        prefix={<SearchOutlined className="text-[#98a2b3]" />}
                        placeholder="Бренд"
                        value={brandQuery}
                        onChange={(event) => setBrandQuery(event.target.value)}
                        className="reviews-filter-search"
                    />
                    <p className="mt-3 mb-3 text-[13px] leading-5 text-[#667085]">
                        Показываем только бренды, где вы — официальный представитель
                    </p>
                    <div className="space-y-1">
                        {filteredBrands.map((brand) => (
                            <OptionRow
                                key={brand.id}
                                checked={(draft.brands || []).includes(brand.id)}
                                onChange={() =>
                                    setDraft((current) => ({
                                        ...current,
                                        brands: toggleInList(current.brands, brand.id),
                                    }))
                                }
                            >
                                <span className="text-[14px] text-[#1a1a1a]">{brand.label}</span>
                            </OptionRow>
                        ))}
                        {filteredBrands.length === 0 ? (
                            <div className="text-[13px] text-[#98a2b3]">Бренды не найдены</div>
                        ) : null}
                    </div>
                </>
            );
        }

        if (category === 'rating') {
            return (
                <>
                    <div className="mb-3 text-[16px] font-semibold text-[#1a1a1a]">Оценка</div>
                    <div className="space-y-1">
                        {[5, 4, 3, 2, 1].map((rating) => (
                            <OptionRow
                                key={rating}
                                checked={(draft.ratings || []).includes(rating)}
                                onChange={() =>
                                    setDraft((current) => ({
                                        ...current,
                                        ratings: toggleInList(current.ratings, rating),
                                    }))
                                }
                            >
                                <span className="w-4 text-[14px] text-[#1a1a1a]">{rating}</span>
                                <StarRow value={rating} />
                            </OptionRow>
                        ))}
                    </div>
                </>
            );
        }

        if (category === 'content') {
            return (
                <>
                    <div className="mb-3 text-[16px] font-semibold text-[#1a1a1a]">Контент</div>
                    <div className="space-y-1">
                        <OptionRow
                            checked={Boolean(draft.onlyWithRating)}
                            onChange={(event) =>
                                setDraft((current) => ({
                                    ...current,
                                    onlyWithRating: event.target.checked,
                                }))
                            }
                        >
                            <span className="text-[14px]">Только с оценкой</span>
                        </OptionRow>
                        <OptionRow
                            checked={Boolean(draft.withText)}
                            onChange={(event) =>
                                setDraft((current) => ({
                                    ...current,
                                    withText: event.target.checked,
                                }))
                            }
                        >
                            <span className="text-[14px]">С текстом</span>
                        </OptionRow>
                        <OptionRow
                            checked={Boolean(draft.withMedia)}
                            onChange={(event) =>
                                setDraft((current) => ({
                                    ...current,
                                    withMedia: event.target.checked,
                                }))
                            }
                        >
                            <span className="text-[14px]">С медиа</span>
                        </OptionRow>
                    </div>
                </>
            );
        }

        if (category === 'order') {
            return (
                <>
                    <div className="mb-3 text-[16px] font-semibold text-[#1a1a1a]">
                        Статус получения
                    </div>
                    <div className="space-y-1">
                        {[
                            { value: 'DELIVERED', label: 'Получен' },
                            { value: 'CANCELLED', label: 'Отменён' },
                        ].map((option) => (
                            <OptionRow
                                key={option.value}
                                checked={(draft.orderStatuses || []).includes(option.value)}
                                onChange={() =>
                                    setDraft((current) => ({
                                        ...current,
                                        orderStatuses: toggleInList(
                                            current.orderStatuses,
                                            option.value
                                        ),
                                    }))
                                }
                            >
                                <span className="text-[14px]">{option.label}</span>
                            </OptionRow>
                        ))}
                    </div>
                </>
            );
        }

        return (
            <>
                <div className="mb-3 text-[16px] font-semibold text-[#1a1a1a]">
                    Статус публикации
                </div>
                <div className="space-y-1">
                    {[
                        { value: 'UNPROCESSED', label: 'Пока не опубликованы' },
                        { value: 'PROCESSED', label: 'Опубликованы' },
                    ].map((option) => (
                        <OptionRow
                            key={option.value}
                            checked={(draft.publicationStatuses || []).includes(option.value)}
                            onChange={() =>
                                setDraft((current) => ({
                                    ...current,
                                    publicationStatuses: toggleInList(
                                        current.publicationStatuses,
                                        option.value
                                    ),
                                }))
                            }
                        >
                            <span className="text-[14px]">{option.label}</span>
                        </OptionRow>
                    ))}
                </div>
            </>
        );
    };

    return (
        <div ref={rootRef} className="relative flex flex-wrap items-center gap-2">
            <style>{`
                .reviews-filter-pill {
                    height: 36px;
                    border-radius: 10px;
                    border: 1px solid #d0d5dd;
                    background: #fff;
                    color: #1a1a1a;
                    padding: 0 12px;
                    font-size: 14px;
                    box-shadow: none;
                }
                .reviews-filter-pill:hover {
                    border-color: #98a2b3 !important;
                    color: #1a1a1a !important;
                }
                .reviews-filter-pill--active {
                    border-color: #005bff !important;
                    color: #005bff !important;
                }
                .reviews-filter-search.ant-input-affix-wrapper {
                    border-radius: 10px;
                    height: 40px;
                }
                .reviews-filter-panel .ant-checkbox-inner {
                    width: 18px;
                    height: 18px;
                    border-radius: 4px;
                    border-color: #9ec0ff;
                    background: #f4f8ff;
                }
                .reviews-filter-panel .ant-checkbox-checked .ant-checkbox-inner {
                    background: #005bff;
                    border-color: #005bff;
                }
            `}</style>

            <Button
                className={pillClass(open || selectedCount > 0)}
                onClick={() => {
                    setPeriodOpen(false);
                    setOpen((current) => !current);
                }}
            >
                <span className="inline-flex items-center gap-2">
                    Фильтры
                    {selectedCount > 0 ? (
                        <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#005bff] px-1.5 text-[11px] font-semibold text-white">
                            {selectedCount}
                        </span>
                    ) : null}
                    {open ? <UpOutlined className="text-[10px]" /> : <DownOutlined className="text-[10px]" />}
                </span>
            </Button>

            <Dropdown
                open={periodOpen}
                onOpenChange={(next) => {
                    setPeriodOpen(next);
                    if (next) setOpen(false);
                }}
                trigger={['click']}
                dropdownRender={() => (
                    <div className="rounded-2xl border border-[#e4e7ec] bg-white p-3 shadow-lg">
                        <RangePicker
                            value={value?.published_range}
                            open
                            getPopupContainer={(node) => node.parentNode}
                            onChange={(range) => {
                                onApply?.({
                                    ...(value || EMPTY_REVIEW_FILTERS),
                                    published_range: range,
                                });
                                if (range?.[0] && range?.[1]) setPeriodOpen(false);
                            }}
                            format="DD.MM.YYYY"
                        />
                    </div>
                )}
            >
                <Button className={pillClass(Boolean(value?.published_range?.length) || periodOpen)}>
                    <span className="inline-flex items-center gap-2">
                        {periodLabel}
                        {periodOpen ? (
                            <UpOutlined className="text-[10px]" />
                        ) : (
                            <DownOutlined className="text-[10px]" />
                        )}
                    </span>
                </Button>
            </Dropdown>

            <Dropdown
                menu={{
                    items: [
                        { key: 'DESC', label: 'Сначала новые' },
                        { key: 'ASC', label: 'Сначала старые' },
                    ],
                    selectable: true,
                    selectedKeys: [value?.sort_dir || 'DESC'],
                    onClick: ({ key }) =>
                        onApply?.({
                            ...(value || EMPTY_REVIEW_FILTERS),
                            sort_dir: key,
                        }),
                }}
                trigger={['click']}
            >
                <Button className={pillClass(false)}>
                    <span className="inline-flex items-center gap-2">
                        <SwapOutlined rotate={90} />
                        <DownOutlined className="text-[10px]" />
                    </span>
                </Button>
            </Dropdown>

            {open ? (
                <div className="reviews-filter-panel absolute left-0 top-[calc(100%+8px)] z-30 w-[min(620px,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-[#e4e7ec] bg-white shadow-[0_16px_48px_rgba(16,24,40,0.16)]">
                    <div className="flex min-h-[292px]">
                        <aside className="w-[228px] shrink-0 border-r border-[#eef0f4] p-2">
                            {CATEGORIES.map((item) => {
                                const active = category === item.key;
                                return (
                                    <button
                                        key={item.key}
                                        type="button"
                                        onClick={() => setCategory(item.key)}
                                        className={`relative mb-1 flex w-full items-center rounded-xl px-3 py-[10px] text-left text-[14px] leading-5 transition ${
                                            active
                                                ? 'border border-[#bfd0ff] bg-white font-medium text-[#005bff]'
                                                : 'border border-transparent text-[#1a1a1a] hover:bg-[#f7f8fa]'
                                        }`}
                                    >
                                        {active ? (
                                            <span className="absolute bottom-[6px] left-0 top-[6px] w-[3px] rounded-r bg-[#005bff]" />
                                        ) : null}
                                        {item.label}
                                    </button>
                                );
                            })}
                        </aside>
                        <div className="min-w-0 flex-1 px-5 py-4">{renderBody()}</div>
                    </div>
                    <div className="flex items-center justify-between gap-3 border-t border-[#eef0f4] px-4 py-3">
                        <div className="flex items-center gap-2">
                            <Button
                                type="primary"
                                className="!h-9 !rounded-lg !border-[#005bff] !bg-[#005bff] !px-4"
                                onClick={applyDraft}
                            >
                                Применить
                            </Button>
                            <Button
                                className="!h-9 !rounded-lg !border-transparent !bg-[#f2f4f7] !px-4 !text-[#1a1a1a]"
                                onClick={resetDraft}
                            >
                                Сбросить
                            </Button>
                        </div>
                        <span className="text-[13px] text-[#98a2b3]">
                            Выбрано фильтров: {draftCount}
                        </span>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default ReviewsFilterBar;
